/**
 * Normalizes nav preset links once at the data layer.
 * Handles admin-entered paths like `to: "/#features"` with `hash: null`.
 */

const preparedLinksCache = new Map();

function slugify(label) {
  return (label || '').toLowerCase().replace(/\s+/g, '-');
}

export function parseNavTarget(link) {
  let pathname = link?.to || '/';
  let hash = link?.hash || null;

  if (!hash && pathname.includes('#')) {
    const idx = pathname.indexOf('#');
    hash = pathname.slice(idx + 1);
    pathname = pathname.slice(0, idx) || '/';
  }

  if (hash) {
    hash = hash.replace(/^#/, '');
  }

  const routerTo = hash ? { pathname, hash: `#${hash}` } : pathname;
  const href = hash ? `${pathname === '/' ? '/' : pathname}#${hash}` : pathname;

  return { pathname, hash, routerTo, href };
}

export function prepareNavLinks(rawLinks, cacheKey = null) {
  if (cacheKey && preparedLinksCache.has(cacheKey)) {
    return preparedLinksCache.get(cacheKey);
  }

  const prepared = (rawLinks || [])
    .filter((l) => l.is_active !== false)
    .sort((a, b) => (a.order || 0) - (b.order || 0))
    .map((link) => {
      const target = parseNavTarget(link);
      const children = (link.children || [])
        .filter((c) => c.is_active !== false)
        .map((child) => {
          const childTarget = parseNavTarget(child);
          return Object.freeze({
            id: child.id || `${child.label}-${childTarget.href}`,
            label: child.label,
            openInNewTab: !!child.open_in_new_tab,
            testId: `nav-mobile-link-${slugify(child.label)}`,
            ...childTarget,
          });
        });

      return Object.freeze({
        id: link.id,
        label: link.label,
        openInNewTab: !!link.open_in_new_tab,
        testId: `nav-mobile-link-${slugify(link.label)}`,
        megaMenuColumns: link.mega_menu_columns || 2,
        megaMenuBlurb: link.mega_menu_blurb || null,
        children: Object.freeze(children),
        ...target,
      });
    });

  const frozen = Object.freeze(prepared);
  if (cacheKey) {
    preparedLinksCache.set(cacheKey, frozen);
  }
  return frozen;
}

export function clearPreparedNavLinksCache() {
  preparedLinksCache.clear();
}
